/**
 * Analytics Service for RBAC Frontend
 * Tracks user actions, bid amounts, payment amounts with local storage and Google Analytics
 */

import { getAuth } from 'firebase/auth';
import { apiService } from './apiService';
import { getUserTrackingInfo, createTrackingData } from '../utils/userTracking';

class AnalyticsService {
  constructor() {
    this.gaId = process.env.REACT_APP_GA_ID || 'G-XXXXXXXXXX';
    this.isInitialized = false;
    this.userInfo = null;
    this.sessionId = this.generateSessionId();
    this.localStorageKey = 'analytics_events';
    this.maxLocalEvents = 100; // Maximum events to store locally
  }

  /**
   * Generate unique session ID
   */
  generateSessionId() {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Initialize Google Analytics
   */
  init() {
    if (this.isInitialized) return;

    // Load Google Analytics script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${this.gaId}`;
    document.head.appendChild(script);

    // Initialize gtag
    window.dataLayer = window.dataLayer || [];
    function gtag() {
      window.dataLayer.push(arguments);
    }
    window.gtag = gtag;

    gtag('js', new Date());
    gtag('config', this.gaId, {
      page_title: document.title,
      page_location: window.location.href,
    });

    this.isInitialized = true;
    console.log('📊 Google Analytics initialized for RBAC Frontend');
  }

  /**
   * Set user information for tracking
   */
  setUser(user) {
    if (!user) return;

    console.log('📊 Setting analytics user:', user);

    this.userInfo = {
      uid: user.uid,
      email: user.email,
      name: user.displayName || user.email,
      roles: user.roles || [],
      organization: user.organization || null
    };

    // Set user properties in GA
    if (window.gtag) {
      window.gtag('config', this.gaId, {
        user_id: user.uid,
        custom_map: {
          'custom_parameter_1': 'user_roles',
          'custom_parameter_2': 'organization'
        }
      });

      // Set user properties
      window.gtag('event', 'user_login', {
        user_id: user.uid,
        user_email: user.email,
        user_roles: JSON.stringify(user.roles || []),
        organization: user.organization || 'unknown'
      });
    }
  }

  /**
   * Store event locally and send to backend
   */
  async storeEvent(eventData) {
    // Determine dealer name based on user role
    const isDealerRole = this.userInfo?.roles?.includes('orguserdealer') || 
                        this.userInfo?.roles?.includes('dealer') || 
                        this.userInfo?.role === 'orguserdealer' || 
                        this.userInfo?.role === 'dealer';
    const dealerName = isDealerRole ? 
      (this.userInfo?.name || this.userInfo?.email) : 
      (this.userInfo?.dealerName || this.userInfo?.dealer_name || this.userInfo?.email);

    const fullEventData = {
      ...eventData,
      timestamp: new Date().toISOString(),
      sessionId: this.sessionId,
      pageUrl: window.location.href,
      userAgent: navigator.userAgent,
      userId: this.userInfo?.uid,
      userEmail: this.userInfo?.email,
      userName: this.userInfo?.name,
      dealerName: dealerName,
      userRoles: this.userInfo?.roles || [],
      organization: this.userInfo?.organization
    };

    console.log('📊 Storing analytics event:', fullEventData);

    // Detailed logging for gate pass events
    if (eventData.eventName === 'gate_pass_requested') {
      console.log('🚗 Gate Pass Event - Full Event Data:');
      console.log('🚗 Event Name:', fullEventData.eventName);
      console.log('🚗 Event Category:', fullEventData.eventCategory);
      console.log('🚗 Custom Parameters:', JSON.stringify(fullEventData.customParameters, null, 2));
      console.log('🚗 Car Make:', fullEventData.customParameters?.carMake);
      console.log('🚗 Car Model:', fullEventData.customParameters?.carModel);
      console.log('🚗 Registration Number:', fullEventData.customParameters?.registrationNumber);
      console.log('🚗 Dealer Name:', fullEventData.customParameters?.dealerName);
      console.log('🚗 Supplier Name:', fullEventData.customParameters?.supplierName);
      console.log('🚗 Location:', fullEventData.customParameters?.location);
      console.log('🚗 Yard Name:', fullEventData.customParameters?.yardName);
    }

    // Store locally
    this.storeEventLocally(fullEventData);

    // Send to backend only if authentication is available
    const authToken = localStorage.getItem('authToken');
    if (authToken) {
      try {
        await apiService.post('storeAnalyticsEvent', fullEventData);
        console.log('📊 Analytics event sent to backend successfully');
      } catch (error) {
        console.error('Failed to send analytics event to backend:', error);
        // Event is still stored locally, so we can retry later
      }
    } else {
      console.log('📊 No auth token available, storing analytics event locally only');
    }
  }

  /**
   * Store event in localStorage
   */
  storeEventLocally(eventData) {
    try {
      const existingEvents = JSON.parse(localStorage.getItem(this.localStorageKey) || '[]');
      existingEvents.push(eventData);

      // Keep only the most recent events
      if (existingEvents.length > this.maxLocalEvents) {
        existingEvents.splice(0, existingEvents.length - this.maxLocalEvents);
      }

      localStorage.setItem(this.localStorageKey, JSON.stringify(existingEvents));
    } catch (error) {
      console.error('Failed to store analytics event locally:', error);
    }
  }

  /**
   * Get locally stored events
   */
  getLocalEvents() {
    try {
      return JSON.parse(localStorage.getItem(this.localStorageKey) || '[]');
    } catch (error) {
      console.error('Failed to retrieve local analytics events:', error);
      return [];
    }
  }

  /**
   * Clear local events
   */
  clearLocalEvents() {
    localStorage.removeItem(this.localStorageKey);
  }

  /**
   * Track page views
   */
  trackPageView(pageName, pagePath = null) {
    const eventData = {
      eventName: 'page_view',
      eventCategory: 'Navigation',
      eventLabel: pageName,
      pagePath: pagePath || window.location.pathname
    };

    this.storeEvent(eventData);

    if (window.gtag) {
      window.gtag('event', 'page_view', {
        page_title: pageName,
        page_location: pagePath || window.location.href,
        page_path: pagePath || window.location.pathname,
        user_id: this.userInfo?.uid,
        user_roles: JSON.stringify(this.userInfo?.roles || [])
      });
    }
  }

  /**
   * Track user actions
   */
  trackAction(action, category = 'User Action', label = null, value = null) {
    const eventData = {
      eventName: action,
      eventCategory: category,
      eventLabel: label,
      eventValue: value
    };

    this.storeEvent(eventData);

    if (window.gtag) {
      window.gtag('event', action, {
        event_category: category,
        event_label: label,
        value: value,
        user_id: this.userInfo?.uid,
        user_roles: JSON.stringify(this.userInfo?.roles || []),
        organization: this.userInfo?.organization
      });
    }
  }

  /**
   * Track bid-related actions
   */
  trackBidAction(action, bidAmount, carId = null, auctionId = null) {
    const eventData = {
      eventName: action,
      eventCategory: 'Bidding',
      eventLabel: `Bid: ${bidAmount}`,
      eventValue: bidAmount,
      customParameters: {
        carId,
        auctionId,
        currency: 'INR'
      }
    };

    this.storeEvent(eventData);

    if (window.gtag) {
      window.gtag('event', action, {
        event_category: 'Bidding',
        event_label: `Bid: ${bidAmount}`,
        value: bidAmount,
        currency: 'INR',
        car_id: carId,
        auction_id: auctionId,
        user_id: this.userInfo?.uid,
        user_roles: JSON.stringify(this.userInfo?.roles || []),
        organization: this.userInfo?.organization
      });
    }
  }

  /**
   * Track Add to My Cars action
   */
  trackAddToMyCars(carData) {
    const {
      carMake,
      carModel,
      carYear,
      dealerName,
      supplierName,
      carId,
      registrationNumber,
      price,
      location
    } = carData;

    const eventData = {
      eventName: 'add_to_my_cars',
      eventCategory: 'Car Management',
      eventLabel: `${carMake} ${carModel} ${carYear}`,
      eventValue: price || 0,
      customParameters: {
        carMake: carMake || 'Unknown',
        carModel: carModel || 'Unknown',
        carYear: carYear || 'Unknown',
        dealerName: dealerName || 'Unknown',
        supplierName: supplierName || 'Unknown',
        carId: carId || 'Unknown',
        registrationNumber: registrationNumber || 'Unknown',
        price: price || 0,
        location: location || 'Unknown',
        city: location || 'Unknown',
        timestamp: new Date().toISOString()
      }
    };

    console.log('📊 Tracking Add to My Cars:', eventData);
    this.storeEvent(eventData);

    if (window.gtag) {
      window.gtag('event', 'add_to_my_cars', {
        event_category: 'Car Management',
        event_label: `${carMake} ${carModel} ${carYear}`,
        value: price || 0,
        currency: 'INR',
        car_make: carMake || 'Unknown',
        car_model: carModel || 'Unknown',
        car_year: carYear || 'Unknown',
        dealer_name: dealerName || 'Unknown',
        supplier_name: supplierName || 'Unknown',
        car_id: carId || 'Unknown',
        registration_number: registrationNumber || 'Unknown',
        price: price || 0,
        location: location || 'Unknown',
        user_id: this.userInfo?.uid,
        user_roles: JSON.stringify(this.userInfo?.roles || []),
        organization: this.userInfo?.organization
      });
    }
  }

  /**
   * Track Gate Pass Requested action
   */
  trackGatePassRequested(carData) {
    console.log('🚗 Raw car data received:', carData);
    
    const {
      carMake,
      carModel,
      carYear,
      dealerName,
      supplierName,
      carId,
      registrationNumber,
      price,
      location,
      yardName,
      gatePassType
    } = carData;

    console.log('🚗 Extracted car details:', {
      carMake,
      carModel,
      carYear,
      dealerName,
      supplierName,
      carId,
      registrationNumber,
      price,
      location,
      yardName,
      gatePassType
    });

    const eventData = {
      eventName: 'gate_pass_requested',
      eventCategory: 'Car Management',
      eventLabel: `Gate Pass: ${carMake} ${carModel} ${carYear}`,
      eventValue: price || 0,
      customParameters: {
        carMake: carMake || 'Unknown',
        carModel: carModel || 'Unknown',
        carYear: carYear || 'Unknown',
        dealerName: dealerName || 'Unknown',
        supplierName: supplierName || 'Unknown',
        carId: carId || 'Unknown',
        registrationNumber: registrationNumber || 'Unknown',
        price: price || 0,
        location: location || 'Unknown',
        city: location || 'Unknown',
        yardName: yardName || 'Unknown',
        gatePassType: gatePassType || 'Standard',
        timestamp: new Date().toISOString()
      }
    };

    console.log('📊 Tracking Gate Pass Requested:', eventData);
    this.storeEvent(eventData);

    if (window.gtag) {
      window.gtag('event', 'gate_pass_requested', {
        event_category: 'Car Management',
        event_label: `Gate Pass: ${carMake} ${carModel} ${carYear}`,
        value: price || 0,
        currency: 'INR',
        car_make: carMake || 'Unknown',
        car_model: carModel || 'Unknown',
        car_year: carYear || 'Unknown',
        dealer_name: dealerName || 'Unknown',
        supplier_name: supplierName || 'Unknown',
        car_id: carId || 'Unknown',
        registration_number: registrationNumber || 'Unknown',
        price: price || 0,
        location: location || 'Unknown',
        yard_name: yardName || 'Unknown',
        gate_pass_type: gatePassType || 'Standard',
        user_id: this.userInfo?.uid,
        user_roles: JSON.stringify(this.userInfo?.roles || []),
        organization: this.userInfo?.organization
      });
    }
  }

  /**
   * Track payment-related actions
   */
  trackPaymentAction(action, paymentAmount, paymentMethod = null, transactionId = null) {
    const eventData = {
      eventName: action,
      eventCategory: 'Payment',
      eventLabel: `Payment: ${paymentAmount}`,
      eventValue: paymentAmount,
      customParameters: {
        paymentMethod,
        transactionId,
        currency: 'INR'
      }
    };

    this.storeEvent(eventData);

    if (window.gtag) {
      window.gtag('event', action, {
        event_category: 'Payment',
        event_label: `Payment: ${paymentAmount}`,
        value: paymentAmount,
        currency: 'INR',
        payment_method: paymentMethod,
        transaction_id: transactionId,
        user_id: this.userInfo?.uid,
        user_roles: JSON.stringify(this.userInfo?.roles || []),
        organization: this.userInfo?.organization
      });
    }
  }

  /**
   * Track workflow state changes
   */
  trackWorkflowStateChange(fromState, toState, carId = null) {
    const eventData = {
      eventName: 'workflow_state_change',
      eventCategory: 'Workflow',
      eventLabel: `${fromState} → ${toState}`,
      customParameters: {
        fromState,
        toState,
        carId
      }
    };

    this.storeEvent(eventData);

    if (window.gtag) {
      window.gtag('event', 'workflow_state_change', {
        event_category: 'Workflow',
        event_label: `${fromState} → ${toState}`,
        from_state: fromState,
        to_state: toState,
        car_id: carId,
        user_id: this.userInfo?.uid,
        user_roles: JSON.stringify(this.userInfo?.roles || []),
        organization: this.userInfo?.organization
      });
    }
  }

  /**
   * Track form submissions
   */
  trackFormSubmission(formName, success = true, errorMessage = null) {
    const eventData = {
      eventName: 'form_submission',
      eventCategory: 'Form',
      eventLabel: formName,
      customParameters: {
        success,
        errorMessage
      }
    };

    this.storeEvent(eventData);

    if (window.gtag) {
      window.gtag('event', 'form_submission', {
        event_category: 'Form',
        event_label: formName,
        success: success,
        error_message: errorMessage,
        user_id: this.userInfo?.uid,
        user_roles: JSON.stringify(this.userInfo?.roles || []),
        organization: this.userInfo?.organization
      });
    }
  }

  /**
   * Track file uploads
   */
  trackFileUpload(fileType, fileSize, success = true) {
    const eventData = {
      eventName: 'file_upload',
      eventCategory: 'File Upload',
      eventLabel: fileType,
      eventValue: fileSize,
      customParameters: {
        success
      }
    };

    this.storeEvent(eventData);

    if (window.gtag) {
      window.gtag('event', 'file_upload', {
        event_category: 'File Upload',
        event_label: fileType,
        file_size: fileSize,
        success: success,
        user_id: this.userInfo?.uid,
        user_roles: JSON.stringify(this.userInfo?.roles || []),
        organization: this.userInfo?.organization
      });
    }
  }

  /**
   * Track search actions
   */
  trackSearch(searchTerm, resultsCount = null, category = 'General') {
    const eventData = {
      eventName: 'search',
      eventCategory: 'Search',
      eventLabel: searchTerm,
      customParameters: {
        searchTerm,
        resultsCount,
        category
      }
    };

    this.storeEvent(eventData);

    if (window.gtag) {
      window.gtag('event', 'search', {
        event_category: 'Search',
        event_label: searchTerm,
        search_term: searchTerm,
        results_count: resultsCount,
        user_id: this.userInfo?.uid,
        user_roles: JSON.stringify(this.userInfo?.roles || []),
        organization: this.userInfo?.organization
      });
    }
  }

  /**
   * Track error events
   */
  trackError(errorMessage, errorCode = null, component = null) {
    const eventData = {
      eventName: 'exception',
      eventCategory: 'Error',
      eventLabel: errorMessage,
      customParameters: {
        errorCode,
        component
      }
    };

    this.storeEvent(eventData);

    if (window.gtag) {
      window.gtag('event', 'exception', {
        description: errorMessage,
        fatal: false,
        error_code: errorCode,
        component: component,
        user_id: this.userInfo?.uid,
        user_roles: JSON.stringify(this.userInfo?.roles || []),
        organization: this.userInfo?.organization
      });
    }
  }

  /**
   * Track custom business events
   */
  trackBusinessEvent(eventName, parameters = {}) {
    const eventData = {
      eventName,
      eventCategory: 'Business',
      customParameters: parameters
    };

    this.storeEvent(eventData);

    if (window.gtag) {
      window.gtag('event', eventName, {
        ...parameters,
        user_id: this.userInfo?.uid,
        user_roles: JSON.stringify(this.userInfo?.roles || []),
        organization: this.userInfo?.organization
      });
    }
  }

  /**
   * Track user logout
   */
  trackLogout() {
    const eventData = {
      eventName: 'user_logout',
      eventCategory: 'Authentication'
    };

    this.storeEvent(eventData);

    if (window.gtag) {
      window.gtag('event', 'user_logout', {
        user_id: this.userInfo?.uid,
        user_roles: JSON.stringify(this.userInfo?.roles || []),
        organization: this.userInfo?.organization
      });
    }

    this.userInfo = null;
  }

  /**
   * Get analytics summary from backend
   */
  async getAnalyticsSummary(startDate = null, endDate = null, eventCategory = null) {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      if (eventCategory) params.append('eventCategory', eventCategory);

      const response = await apiService.get(`getAnalyticsSummary?${params.toString()}`);
      return response;
    } catch (error) {
      console.error('Failed to fetch analytics summary:', error);
      throw error;
    }
  }

  /**
   * Get analytics events from backend
   */
  async getAnalyticsEvents(startDate = null, endDate = null, eventCategory = null, limit = 100, offset = 0) {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      if (eventCategory) params.append('eventCategory', eventCategory);
      params.append('limit', limit);
      params.append('offset', offset);

      const response = await apiService.get(`getAnalyticsEvents?${params.toString()}`);
      return response;
    } catch (error) {
      console.error('Failed to fetch analytics events:', error);
      throw error;
    }
  }

  /**
   * Track car management operations with user and dealer information
   * @param {Object} operationData - Operation specific data
   * @param {Object} userInfo - User information from useAuth hook
   * @param {string} action - Action being performed
   * @param {string} actionType - Type of action
   */
  trackCarManagementOperation(operationData, userInfo, action, actionType) {
    const trackingInfo = getUserTrackingInfo(
      userInfo.currentUser, 
      userInfo.userRoles, 
      userInfo.userRole
    );
    
    const trackingData = createTrackingData(operationData, trackingInfo, action, actionType);
    
    // Prefer explicit dealer/user overrides from operationData if provided
    const effectiveUserName = operationData?.userName || trackingInfo.userName;
    const effectiveDealerName = operationData?.dealerName || trackingInfo.dealerName;

    const eventData = {
      eventName: action.toLowerCase().replace(/\s+/g, '_'),
      eventCategory: 'Car Management',
      eventLabel: `${effectiveUserName} - ${effectiveDealerName}`,
      customParameters: {
        ...trackingData,
        car_management_action: action,
        car_management_type: actionType,
        // Include the effective user and dealer names in customParameters
        userName: effectiveUserName,
        dealerName: effectiveDealerName
      }
    };

    console.log('📊 Tracking Car Management Operation:', eventData);
    console.log('📊 Effective User Name:', effectiveUserName);
    console.log('📊 Effective Dealer Name:', effectiveDealerName);
    console.log('📊 Custom Parameters:', eventData.customParameters);
    this.storeEvent(eventData);

    if (window.gtag) {
      window.gtag('event', action.toLowerCase().replace(/\s+/g, '_'), {
        event_category: 'Car Management',
        event_label: `${effectiveUserName} - ${effectiveDealerName}`,
        user_id: trackingInfo.userId,
        user_name: effectiveUserName,
        dealer_name: effectiveDealerName,
        user_role: trackingInfo.userRole,
        is_dealer_role: trackingInfo.isDealerRole,
        organization_id: trackingInfo.organizationId,
        car_management_action: action,
        car_management_type: actionType,
        ...operationData
      });
    }
  }

  /**
   * Track Add to My Cars with enhanced user/dealer tracking
   */
  trackAddToMyCarsEnhanced(carData, userInfo) {
    const trackingInfo = getUserTrackingInfo(
      userInfo.currentUser, 
      userInfo.userRoles, 
      userInfo.userRole
    );
    
    const operationData = {
      carId: carData.carId,
      carMake: carData.carMake,
      carModel: carData.carModel,
      carYear: carData.carYear,
      registrationNumber: carData.registrationNumber,
      price: carData.price,
      location: carData.location,
      supplierName: carData.supplierName
    };
    
    this.trackCarManagementOperation(operationData, userInfo, 'Add to My Cars', 'addtomycars');
  }

  /**
   * Track Gatepass Request with enhanced user/dealer tracking
   */
  trackGatePassRequestEnhanced(carData, userInfo) {
    const trackingInfo = getUserTrackingInfo(
      userInfo.currentUser, 
      userInfo.userRoles, 
      userInfo.userRole
    );
    
    const operationData = {
      carId: carData.carId,
      carMake: carData.carMake,
      carModel: carData.carModel,
      carYear: carData.carYear,
      registrationNumber: carData.registrationNumber,
      location: carData.location
    };
    
    this.trackCarManagementOperation(operationData, userInfo, 'Gatepass Request', 'gatepass_request');
  }

  /**
   * Track Bid Placement with enhanced user/dealer tracking
   */
  trackBidPlacementEnhanced(bidData, userInfo) {
    const trackingInfo = getUserTrackingInfo(
      userInfo.currentUser, 
      userInfo.userRoles, 
      userInfo.userRole
    );
    
    const operationData = {
      carId: bidData.carId,
      bidAmount: bidData.bidAmount,
      auctionId: bidData.auctionId,
      carMake: bidData.carMake,
      carModel: bidData.carModel,
      carYear: bidData.carYear,
      registrationNumber: bidData.registrationNumber
    };
    
    this.trackCarManagementOperation(operationData, userInfo, 'Bid Placement', 'bid_placement');
  }

  /**
   * Track Payment Submission with enhanced user/dealer tracking
   */
  trackPaymentSubmissionEnhanced(paymentData, userInfo) {
    const trackingInfo = getUserTrackingInfo(
      userInfo.currentUser, 
      userInfo.userRoles, 
      userInfo.userRole
    );
    
    const operationData = {
      carId: paymentData.carId,
      paymentAmount: paymentData.paymentAmount,
      paymentMethod: paymentData.paymentMethod,
      transactionId: paymentData.transactionId,
      carMake: paymentData.carMake,
      carModel: paymentData.carModel,
      carYear: paymentData.carYear,
      registrationNumber: paymentData.registrationNumber
    };
    
    this.trackCarManagementOperation(operationData, userInfo, 'Payment Submission', 'payment_submission');
  }

  /**
   * Track Bid Award with enhanced user/dealer tracking
   */
  trackBidAwardEnhanced(awardData, userInfo) {
    const trackingInfo = getUserTrackingInfo(
      userInfo.currentUser, 
      userInfo.userRoles, 
      userInfo.userRole
    );
    
    const operationData = {
      carId: awardData.carId,
      bidWinAmount: awardData.bidWinAmount,
      awardedAmount: awardData.awardedAmount,
      deposit: awardData.deposit,
      daysToCompleteKyc: awardData.daysToCompleteKyc,
      carMake: awardData.carMake,
      carModel: awardData.carModel,
      carYear: awardData.carYear,
      registrationNumber: awardData.registrationNumber,
      // Allow caller to provide explicit dealer/user to avoid mixing with logged-in user
      dealerName: awardData.dealerName || undefined,
      userName: awardData.userName || undefined
    };
    
    this.trackCarManagementOperation(operationData, userInfo, 'Bid Award', 'bid_award');
  }

  /**
   * Track Delivery Confirmation with enhanced user/dealer tracking
   */
  trackDeliveryConfirmationEnhanced(deliveryData, userInfo) {
    const trackingInfo = getUserTrackingInfo(
      userInfo.currentUser, 
      userInfo.userRoles, 
      userInfo.userRole
    );
    
    const operationData = {
      carId: deliveryData.carId,
      deliveryDate: deliveryData.deliveryDate,
      deliveryPhotos: deliveryData.deliveryPhotos,
      carMake: deliveryData.carMake,
      carModel: deliveryData.carModel,
      carYear: deliveryData.carYear,
      registrationNumber: deliveryData.registrationNumber
    };
    
    this.trackCarManagementOperation(operationData, userInfo, 'Delivery Confirmation', 'delivery_confirmation');
  }

  /**
   * Track NOC Operations with enhanced user/dealer tracking
   */
  trackNOCOperationEnhanced(nocData, userInfo, action) {
    const trackingInfo = getUserTrackingInfo(
      userInfo.currentUser, 
      userInfo.userRoles, 
      userInfo.userRole
    );
    
    const operationData = {
      carId: nocData.carId,
      nocType: nocData.nocType,
      documentUrls: nocData.documentUrls,
      carMake: nocData.carMake,
      carModel: nocData.carModel,
      carYear: nocData.carYear,
      registrationNumber: nocData.registrationNumber
    };
    
    this.trackCarManagementOperation(operationData, userInfo, action, 'noc_operation');
  }

  /**
   * Track Payment Approval with enhanced user/dealer tracking
   */
  trackPaymentApprovalEnhanced(approvalData, userInfo) {
    // Debug: Log the approval data to see what values we're receiving
    console.log('🔍 Analytics Service - Payment Approval Data:', approvalData);
    console.log('🔍 Analytics Service - Amount fields:', {
      amount: approvalData.amount,
      price: approvalData.price,
      transactionAmount: approvalData.transactionAmount,
      canVerifyPayment: approvalData.canVerifyPayment
    });
    
    const operationData = {
      carId: approvalData.carId,
      carMake: approvalData.carMake,
      carModel: approvalData.carModel,
      carYear: approvalData.carYear,
      registrationNumber: approvalData.registrationNumber,
      city: approvalData.canVerifyPayment, // Using canVerifyPayment as city
      price: approvalData.amount, // Store as price for analytics dashboard
      amount: approvalData.amount, // Also store as amount
      approvalComment: approvalData.approvalComment,
      transactionAmount: approvalData.transactionAmount,
      bankReferenceId: approvalData.bankReferenceId,
      dealerName: approvalData.dealerName,
      dealerEmail: approvalData.dealerEmail
    };
    
    console.log('🔍 Analytics Service - Operation Data:', operationData);
    
    this.trackCarManagementOperation(operationData, userInfo, 'Payment Approval', 'payment_approval');
  }
}

// Create singleton instance
const analyticsService = new AnalyticsService();

export default analyticsService;
